// src/contexts/CustomerAuthContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

const CustomerAuthContext = createContext();

// Custom hook to use customer auth context
export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // -----------------------------------------------
  // CUSTOMER LOGIN
  // -----------------------------------------------
  const loginCustomer = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      // Query customers collection for matching email
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('portalEmail', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('No account found with this email address.');
      }

      const customerDoc = querySnapshot.docs[0];
      const data = customerDoc.data();

      // Check if portal is enabled
      if (!data.portalEnabled) {
        throw new Error('Portal access is not enabled for your account. Please contact support.');
      }

      // Verify password (simple comparison - in production use bcrypt or similar)
      if (data.portalPassword !== password) {
        throw new Error('Incorrect password. Please try again.');
      }

      // Set customer session
      const customerInfo = {
        id: customerDoc.id,
        email: data.portalEmail,
        name: data.name,
        phone: data.phone,
        address: data.address,
      };

      setCustomer(customerInfo);
      setCustomerData(data);

      // Store in sessionStorage for persistence
      sessionStorage.setItem('customerSession', JSON.stringify(customerInfo));

      setLoading(false);
      return { success: true };

    } catch (err) {
      console.error('Customer login error:', err);
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // -----------------------------------------------
  // CUSTOMER LOGOUT
  // -----------------------------------------------
  const logoutCustomer = useCallback(() => {
    setCustomer(null);
    setCustomerData(null);
    setError(null);
    sessionStorage.removeItem('customerSession');
  }, []);

  // -----------------------------------------------
  // RESTORE SESSION ON MOUNT
  // -----------------------------------------------
  React.useEffect(() => {
    const storedSession = sessionStorage.getItem('customerSession');
    if (storedSession) {
      try {
        const customerInfo = JSON.parse(storedSession);
        setCustomer(customerInfo);
        
        // Fetch full customer data
        const fetchCustomerData = async () => {
          const customerDoc = await getDoc(doc(db, 'customers', customerInfo.id));
          if (customerDoc.exists()) {
            setCustomerData(customerDoc.data());
          }
        };
        fetchCustomerData();
      } catch (err) {
        console.error('Error restoring session:', err);
        sessionStorage.removeItem('customerSession');
      }
    }
  }, []);

  // -----------------------------------------------
  // REFRESH CUSTOMER DATA
  // -----------------------------------------------
  const refreshCustomerData = useCallback(async () => {
    if (!customer) return;

    try {
      const customerDoc = await getDoc(doc(db, 'customers', customer.id));
      if (customerDoc.exists()) {
        setCustomerData(customerDoc.data());
      }
    } catch (err) {
      console.error('Error refreshing customer data:', err);
    }
  }, [customer]);

  // Context value
  const value = {
    // State
    customer,
    customerData,
    loading,
    error,

    // Functions
    loginCustomer,
    logoutCustomer,
    refreshCustomerData,

    // Helpers
    isAuthenticated: !!customer,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export default CustomerAuthContext;